import { ChangeDetectionStrategy, Component, HostListener, input, output } from '@angular/core';

/**
 * Modal shown after a marker is dropped: lets the reporter choose how to capture
 * the page (immediate screenshot, delayed screenshot, or a short screen
 * recording), or — a quiet link below them — to leave the screenshot out.
 * Presentational — it just emits the chosen option; the host runs
 * the actual capture.
 */
@Component({
  selector: 'app-capture-screenshot',
  templateUrl: './capture-screenshot.html',
  styleUrls: ['./capture-screenshot.scss', './capture-screenshot.premium.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaptureScreenshot {
  /** Whether the modal is shown. */
  readonly open = input(false);

  /** The marker number this capture is attached to (shown in the copy). */
  readonly markerNumber = input(0);

  /**
   * Whether native screen capture is available. When false, the Delay + Video
   * options are hidden, a "not available" warning is shown, and a manual
   * screenshot-upload option is offered instead.
   */
  readonly nativeCaptureAvailable = input(true);

  /** Capture the page exactly as it looks now. */
  readonly captureNow = output<void>();

  /** Capture after a 3-second countdown. */
  readonly captureDelay = output<void>();

  /** Record up to 60 seconds of the screen. */
  readonly captureVideo = output<void>();

  /** Leave the screenshot out: go straight to the feedback form with none attached. */
  readonly skipCapture = output<void>();

  /** Upload a screenshot taken manually with the device (no native capture). */
  readonly uploadScreenshot = output<void>();

  /** Dismiss without capturing (Cancel / scrim / Esc). */
  readonly cancel = output<void>();

  /**
   * Keyboard shortcuts while the modal is open. With native capture: 1 = now,
   * 2 = delay, 3 = video. Without it: 1 = attempt screenshot, 2 = upload.
   * (Esc is handled by the host, which owns the open/cancel state.)
   */
  @HostListener('document:keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {
    if (!this.open()) {
      return;
    }
    if (!this.nativeCaptureAvailable()) {
      switch (event.key) {
        case '1':
          event.preventDefault();
          this.captureNow.emit();
          break;
        case '2':
          event.preventDefault();
          this.uploadScreenshot.emit();
          break;
      }
      return;
    }
    switch (event.key) {
      case '1':
        event.preventDefault();
        this.captureNow.emit();
        break;
      case '2':
        event.preventDefault();
        this.captureDelay.emit();
        break;
      case '3':
        event.preventDefault();
        this.captureVideo.emit();
        break;
    }
  }
}
